export const COMMANDS = Object.freeze({
  'pt-BR': {ai:'Quero tirar uma dúvida sobre o MuPag com a IA.',human:'Quero falar com um atendente humano.'},
  en: {ai:'I want to ask the MuPag AI assistant a product question.',human:'I want to speak to a human agent.'},
});

export class SupportError extends Error {
  constructor(code) {
    super(code === 'configuration' ? 'Invalid support configuration' : code === 'closed' ? 'Support session closed' : 'Support request unavailable');
    this.name = 'SupportError';
    this.code = code;
  }
}

const SEGMENT = /^[a-zA-Z0-9_-]{1,256}$/;
const PROXY_PATH = /^\/api\/v1\/support\/(?:affiliate\/client|client\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/i;
export function validateSession(value) {
  if (!value || value.enabled !== true) return {enabled:false};
  try {
    if(value.proxyPath!==undefined){
      if(typeof value.proxyPath!=='string'||!PROXY_PATH.test(value.proxyPath))throw new Error();
      return {enabled:true,proxyPath:value.proxyPath};
    }
    const url = new URL(value.baseUrl);
    const local = ['localhost','127.0.0.1','[::1]'].includes(url.hostname);
    if ((url.protocol !== 'https:' && !(url.protocol === 'http:' && local)) ||
        url.username || url.password || url.search || url.hash ||
        !['','/'].includes(url.pathname) || !SEGMENT.test(value.inboxIdentifier)) throw new Error();
    if ((value.identifier || value.identifierHash) &&
        (!SEGMENT.test(value.identifier) || !/^[a-f0-9]{64}$/i.test(value.identifierHash))) throw new Error();
    return {enabled:true,baseUrl:url.origin,inboxIdentifier:value.inboxIdentifier,
      ...(value.identifier ? {identifier:value.identifier,identifierHash:value.identifierHash} : {})};
  } catch {throw new SupportError('configuration');}
}

async function boundedJson(response) {
  if (!response.body) throw new SupportError('unavailable');
  const reader=response.body.getReader();
  let total=0;
  const chunks=[];
  try {
    while (true) {
      const {done,value}=await reader.read();
      if (done) break;
      total+=value.byteLength;
      if (total>512*1024) {await reader.cancel();throw new SupportError('unavailable');}
      chunks.push(value);
    }
    const bytes=new Uint8Array(total);
    let offset=0;
    for(const chunk of chunks){bytes.set(chunk,offset);offset+=chunk.length;}
    return JSON.parse(new TextDecoder().decode(bytes));
  } finally {reader.releaseLock();}
}

// All contact/conversation credentials live in this closure, never in browser storage or analytics.
export function createSupportClient(rawSession, fetchImpl = globalThis.fetch) {
  const session=validateSession(rawSession);
  rawSession=undefined;
  const lifecycle=new AbortController();
  let sourceId, conversationId, currentMode, starting, verified=false;
  let contactOperation=globalThis.crypto.randomUUID();
  let conversationOperation=globalThis.crypto.randomUUID();
  const starterOperations=new Map();
  let conversationUncertain=false;
  let pendingSend, sending=false, latestMessageId=0, terminalCode;
  let base=session.enabled ? session.proxyPath??(session.baseUrl+'/public/api/v1/inboxes/'+encodeURIComponent(session.inboxIdentifier)) : '';
  const contactPath=() => '/contacts/'+(session.proxyPath?'current':encodeURIComponent(sourceId));
  const conversationPath=() => contactPath()+'/conversations/'+conversationId;
  function assertActive() {
    if(lifecycle.signal.aborted) throw new SupportError(terminalCode??'closed');
    if(!session.enabled) throw new SupportError('unavailable');
  }
  function dispose(code='closed'){
    terminalCode=code;
    lifecycle.abort();sourceId=undefined;conversationId=undefined;currentMode=undefined;
    starterOperations.clear();pendingSend=undefined;contactOperation=undefined;conversationOperation=undefined;base='';
    for(const key of Object.keys(session))delete session[key];
  }
  function normalizeMessage(message){
    const normalized={...message,message_type:message.message_type==='incoming'?0:message.message_type==='outgoing'?1:message.message_type};
    if(Number.isSafeInteger(normalized.id)&&normalized.id>0)latestMessageId=Math.max(latestMessageId,normalized.id);
    return normalized;
  }
  async function request(path, method='GET', body, operationId) {
    assertActive();
    let accepted=false;
    try {
      const response=await fetchImpl(base+path,{
        method,mode:session.proxyPath?'same-origin':'cors',credentials:session.proxyPath?'same-origin':'omit',cache:'no-store',redirect:'error',
        headers:{accept:'application/json',...(body ? {'content-type':'application/json'} : {}),
          ...(session.proxyPath&&sourceId?{'x-mupag-support-ticket':sourceId}:{}),
          ...(session.proxyPath&&method!=='GET'?{'idempotency-key':operationId??globalThis.crypto.randomUUID()}:{})},
        signal:AbortSignal.any([lifecycle.signal,AbortSignal.timeout(12000)]),
        ...(body ? {body:JSON.stringify(body)} : {}),
      });
      if(!response.ok){
        if(response.status===401||response.status===403){
          dispose('session_expired');
          throw new SupportError('session_expired');
        }
        // Response cleanup cannot erase an HTTP rejection already received.
        try{await response.body?.cancel();}catch{}
        if(method==='POST'&&(response.status>=500||response.status===408))throw new SupportError('delivery_uncertain');
        if(response.status===422)throw new SupportError('sensitive_input');
        if(response.status===400)throw new SupportError('invalid_message');
        throw new SupportError(response.status===429 ? 'rate_limited' : 'unavailable');
      }
      accepted=true;
      const result=await boundedJson(response);
      assertActive();
      return result;
    } catch(error) {
      if(lifecycle.signal.aborted) throw new SupportError(terminalCode??'closed');
      if(accepted&&method==='POST')throw new SupportError('delivery_uncertain');
      if(error instanceof SupportError) throw error;
      throw new SupportError(method==='POST' ? 'delivery_uncertain' : 'unavailable');
    }
  }
  async function connect(locale) {
    const identity=session.identifier
      ? {identifier:session.identifier,identifier_hash:session.identifierHash,name:'MuPag'}
      : {name:'Visitante MuPag'};
    if(!sourceId){
      // Chatwoot reuses the inbox contact for this caller-owned source_id after a lost response.
      const contact=await request('/contacts','POST',{...identity,source_id:contactOperation},contactOperation);
      if(!SEGMENT.test(contact.source_id)) throw new SupportError('unavailable');
      sourceId=contact.source_id;
    }
    // Client API marks HMAC-verified on update. A failed update must not be skipped on retry.
    if(session.identifier&&!verified){await request(contactPath(),'PATCH',identity);verified=true;}
    if(!conversationId){
      if(conversationUncertain){
        const list=await request(contactPath()+'/conversations');
        if(!Array.isArray(list))throw new SupportError('unavailable');
        // Anonymous docs contacts have a fresh random source_id unique to this client instance.
        // Chatwoot's native serializer omits custom_attributes; exactly one result is unambiguous.
        const soleAnonymous=!session.proxyPath&&!session.identifier&&list.length===1;
        const existing=list.find(c=>(c.custom_attributes?.mupag_operation_id===conversationOperation||soleAnonymous)&&Number.isSafeInteger(c.id)&&c.id>0);
        if(!existing)throw new SupportError('delivery_uncertain');
        conversationId=existing.id;
      }else{
        try{
          const conversation=await request(contactPath()+'/conversations','POST',{custom_attributes:{mupag_support:true,mupag_operation_id:conversationOperation,mupag_locale:locale}},conversationOperation);
          if(!Number.isSafeInteger(conversation.id)||conversation.id<1)throw new SupportError('delivery_uncertain');
          conversationId=conversation.id;
        }catch(error){
          if(error.code==='delivery_uncertain')conversationUncertain=true;
          throw error;
        }
      }
    }
  }
  async function postMessage(content, echoId) {
    assertActive();
    const message=await request(conversationPath()+'/messages','POST',{content:content.trim(),echo_id:echoId},echoId);
    if(!message||!Number.isSafeInteger(message.id)||message.id<1) throw new SupportError('delivery_uncertain');
    return normalizeMessage(message);
  }
  async function send(content){
    assertActive();
    if(pendingSend||sending)throw new SupportError('delivery_uncertain');
    if(!conversationId||typeof content!=='string'||!content.trim()||content.length>4000)throw new SupportError('invalid_message');
    sending=true;
    try{
      // Snapshot the latest upstream IDs before POST so a repeated message can be a new intent.
      await readMessages();
      pendingSend={content:content.trim(),echoId:globalThis.crypto.randomUUID(),boundary:latestMessageId};
      const message=await postMessage(pendingSend.content,pendingSend.echoId);
      pendingSend=undefined;
      return message;
    }catch(error){
      if(error.code!=='delivery_uncertain')pendingSend=undefined;
      throw error;
    }finally{sending=false;}
  }
  async function readMessages(before){
    const list=await request(conversationPath()+'/messages'+(before?'?before='+before:''));
    if(!Array.isArray(list))throw new SupportError('unavailable');
    return list.map(normalizeMessage);
  }
  async function reconcileSend(){
    assertActive();
    if(!pendingSend)return null;
    const operation=pendingSend;
    const matches=new Map();
    let before,complete=false;
    for(let page=0;page<25;page++){
      const list=await readMessages(before);
      for(const message of list){
        if(Number.isSafeInteger(message.id)&&message.id>operation.boundary&&!message.private&&message.message_type===0&&message.content===operation.content)matches.set(message.id,message);
      }
      const ids=list.map(m=>m.id).filter(id=>Number.isSafeInteger(id)&&id>0);
      if(!ids.length||Math.min(...ids)<=operation.boundary){complete=true;break;}
      const oldest=Math.min(...ids);
      if(before!==undefined&&oldest>=before)break;
      before=oldest;
    }
    // Absence is not proof of rejection: the original POST may still commit later.
    if(!complete||matches.size!==1)throw new SupportError('delivery_uncertain');
    pendingSend=undefined;
    return [...matches.values()][0];
  }
  async function recoverStarter(operation){
    // echo_id is transient in pinned Chatwoot. Each mode's starter is unique in this conversation.
    let before;
    for(let page=0;page<25;page++){
      const list=await readMessages(before);
      const found=list.find(m=>Number.isSafeInteger(m.id)&&!m.private&&m.message_type===0&&m.content===operation.content);
      if(found)return found;
      const ids=list.map(m=>m.id).filter(id=>Number.isSafeInteger(id)&&id>0);
      if(!ids.length)break;
      const oldest=Math.min(...ids);
      if(before!==undefined&&oldest>=before)break;
      before=oldest;
    }
    // A still-running POST might commit later. Never resubmit it based only on an empty GET.
    throw new SupportError('delivery_uncertain');
  }
  return {
    start(mode,locale='pt-BR'){
      assertActive();
      if(!['human','ai'].includes(mode)) return Promise.reject(new SupportError('invalid_message'));
      if(starting) return starting;
      if(pendingSend||sending)return Promise.reject(new SupportError('delivery_uncertain'));
      if(currentMode===mode && conversationId) return Promise.resolve(null);
      starting=(async()=>{
        await connect(locale==='en'?'en':'pt-BR');
        let operation=starterOperations.get(mode);
        if(!operation){operation={content:(COMMANDS[locale]??COMMANDS['pt-BR'])[mode],echoId:globalThis.crypto.randomUUID(),uncertain:false};starterOperations.set(mode,operation);}
        let message;
        try{message=operation.uncertain?await recoverStarter(operation):await postMessage(operation.content,operation.echoId);}
        catch(error){if(error.code==='delivery_uncertain')operation.uncertain=true;throw error;}
        currentMode=mode;
        return message;
      })().finally(()=>{starting=undefined;});
      return starting;
    },
    send,
    reconcileSend,
    async messages(before){
      assertActive();
      if(!conversationId) return [];
      if(before!==undefined && (!Number.isSafeInteger(before)||before<1)) throw new SupportError('invalid_message');
      const list=await readMessages(before);
      return list.filter(m=>Number.isSafeInteger(m.id)&&!m.private&&[0,1].includes(m.message_type)).sort((a,b)=>a.id-b.id);
    },
    hasConversation:()=>Boolean(conversationId),
    dispose,
  };
}
